import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import { ItemListCell } from '~/components/common';
import { OrdersOutboundCodeCellRenderer } from '~/components/order';

import { maskId, maskName } from '~/utils/common';

import { OUTBOUND_STATUS_BADGE_COLOR } from '~/constants/order';

import type { TypeOrders } from '~/types/order.types';

/**
 * [출고주문관리 > 출고주문 조회] 페이지 테이블
 */
const getOrderOrdersColumnDefs = ({
  hasPermission3PL,
  onClickOutboundCode,
}: {
  hasPermission3PL: boolean;
  onClickOutboundCode: (outboundCode: string) => Promise<void>;
}) => {
  const columnDefs: ColDef<TypeOrders>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      headerCheckboxSelection: hasPermission3PL,
      checkboxSelection: hasPermission3PL,
      width: 72,
    },
    {
      field: 'outbound.outboundStatus',
      headerName: '출고상태',
      width: 82,
      minWidth: 82,
      cellRenderer: Badge,
      cellRendererParams: ({ value }: ICellRendererParams<TypeOrders, TypeOrders['outbound']['outboundStatus']>) => ({
        variant: 'light',
        status: true,
        color: OUTBOUND_STATUS_BADGE_COLOR[value?.code ?? ''],
        children: value?.name,
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'order.clientOrderCode',
      headerName: '판매처 주문번호',
      width: 160,
    },
    {
      field: 'outbound.outboundCode',
      headerName: '출고요청번호',
      width: 164,
      minWidth: 164,
      cellRenderer: OrdersOutboundCodeCellRenderer,
      cellRendererParams: { onClick: onClickOutboundCode },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'delivery.deliveryDate',
      headerName: '배송예정일',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeOrders, TypeOrders['delivery']['deliveryDate']>) => {
        return value ? value.replaceAll('-', '.') : '-';
      },
    },
    {
      field: 'delivery.operation.name',
      headerName: '배송유형',
      width: 80,
    },
    {
      field: 'delivery.courier.name',
      headerName: '배송사',
      width: 88,
    },
    {
      field: 'outbound.clusterCenter.name',
      headerName: '생산센터',
      width: 80,
    },
    {
      field: 'order.orderStoreName',
      headerName: '판매처',
      width: 148,
    },
    {
      field: 'order.paymentAt',
      headerName: '결제완료일시',
      width: 160,
      valueFormatter: ({ value }: ValueFormatterParams<TypeOrders, TypeOrders['order']['paymentAt']>) => {
        return value ? value.replaceAll('-', '.') : '-';
      },
    },
    {
      field: 'receiver.name',
      headerName: '수령자명',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeOrders, TypeOrders['receiver']['name']>) => {
        return maskName(value);
      },
    },
    {
      field: 'orderer.id',
      headerName: '주문자 ID',
      width: 140,
      valueFormatter: ({ value }: ValueFormatterParams<TypeOrders, TypeOrders['orderer']['id']>) => maskId(value),
    },
    {
      field: 'order.orderItems',
      headerName: '상품명',
      minWidth: 240,
      flex: 1,
      cellRenderer: ItemListCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeOrders, TypeOrders['order']['orderItems']>) => ({
        value: data?.order?.orderItems ?? [],
        fieldName: 'itemName',
      }),
      cellStyle: { overflow: 'hidden', whiteSpace: 'normal' },
    },
  ];

  return { columnDefs };
};

export default getOrderOrdersColumnDefs;
